import { createElement, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { ANY, DATE_FORMAT, TIME_FORMAT, MIME_TYPES, MONTHS, DATE_FORMAT_SERVER } from './Constants';
import {
  CodeAndLabel,
  Int32IdAndLabel,
  GuidIdAndLabel,
  GuidIdCodeAndLabel,
  LocalDateLocalDateTuple,
  LocalTimeLocalTimeTuple,
  StationOrLocationItem,
  TimeOfDayRange,
  GuidFilterItemOutput,
  UserPermissionsFormData,
  PermissionListItem,
  UserPermissionsConfiguration,
  AgencyAutocompleteResponseItem,
} from '../integrated-client/models';
import AutocompleteOptionLabel from '../components/Autosuggest/AutocompleteOptionLabel/AutocompleteOptionLabel';
import { IPermissionListItem, IPermissionsData } from '../store/state/UserPermissions/EditorData';
import { BrandAndMarketListItem, BrandAndMarketListItemFilterItemOutputWithPermission } from '../integrated-client';

type GetFilterItemByValueOpts = {
  filters: any[] | null | undefined;
  searchField: string;
  value: any;
  valueFallback: any;
};

export const getId = (() => {
  let generatedId = 0;
  return () => {
    if (generatedId === Number.MAX_VALUE) {
      generatedId = 0;
    } else {
      generatedId++;
    }
    return `id${generatedId}`;
  };
})();

export const dummyFunc = () => {};

export const transformAutocompleteData = (data: StationOrLocationItem[]) => {
  if (data && data.length) {
    return data.map((dataItem: StationOrLocationItem) => ({
      value: JSON.stringify(dataItem),
      label: `(${dataItem.code}) ${dataItem.label}`,
    }));
  }
  return data;
};

export const transformAutocompleteDataWithAgencyGroups = (data: Array<AgencyAutocompleteResponseItem>) => {
  if (data && data.length) {
    return data.map((dataItem: AgencyAutocompleteResponseItem) => ({
      value: dataItem.agency?.id,
      label: `(${dataItem.agency?.code}) ${dataItem.agency?.label}`,
      effective: dataItem.agency?.effective,
      agencyGroup: dataItem.agencyGroup,
    }));
  }
  return data;
};

export const processNullValue = (val: any) => {
  let value = val;
  if (value === 0) return value;
  if (!value || value === ANY) {
    value = null;
  }
  return value;
};

export enum Format {
  Utc,
  AsIs,
}

export const processNumberValue = (value: any, excludeNegative = true) => {
  if (typeof value === 'number') {
    if (excludeNegative && value < 0) {
      return null;
    }
    return value;
  }
  if (value !== null && value !== '' && !isNaN(Number(value))) {
    let val = parseInt(value.replace('+', ''));
    if (excludeNegative) {
      val = parseInt(value.replace('-', ''));
    }
    return val;
  }
  if (value === '') {
    return 0;
  }
  return null;
};

export const capitalizeFirstLetter = (value?: string) => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const isObjectEmpty = (object: any) => {
  let obj = { ...object };
  for (const key in obj) {
    if (Array.isArray(obj[key]) && obj[key].length === 0) obj[key] = undefined;
  }
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};

export const areObjsEqualSimple = (obj1: any, obj2: any) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export const getInfinitySymbol = () => {
  return String.fromCharCode(0x221e);
};

export const getInitialCodeAndLabelValue = () => ({ code: null, label: null } as CodeAndLabel);

export const getInitialStationOrLocationItemValue = () =>
  ({
    code: null,
    label: null,
    isLocation: false,
    effective: false,
  } as StationOrLocationItem);

export const getInitialGuidIdCodeAndLabelValue = () => ({ code: null, label: null, effective: false } as GuidIdCodeAndLabel);

export const getInitialIdAndLabelValue = () => ({ id: null, label: null } as any);

export const parseBoolean = (val: string) => val === 'true';

export const formatDate = (obj: any, format?: string) => dayjs(obj).format(format || DATE_FORMAT);

export const formatDateToTimeStr = (obj: any, format?: string) => dayjs(obj).format(format || TIME_FORMAT);

export const prepareSearchObj = (initialData: any) => {
  let searchObj: any = {};
  Object.keys(initialData).forEach((item) => {
    if (item === 'destinationIsCityCode' || item === 'originIsCityCode') return;
    let dataitem = initialData[item];
    if (typeof dataitem === 'object' && !(dataitem instanceof Date) && dataitem !== null) {
      if (typeof dataitem.isLocation === 'boolean' && item === 'origin') {
        searchObj.originIsCityCode = dataitem.isLocation;
      }

      if (typeof dataitem.isLocation === 'boolean' && item === 'destination') {
        searchObj.destinationIsCityCode = dataitem.isLocation;
      }

      searchObj[item] = dataitem.id === 0 ? dataitem.id : dataitem.code || dataitem.id || (dataitem?.displayText ? dataitem?.value : null) || dataitem.value;
    } else {
      searchObj[item] = dataitem;
    }
  });

  if (initialData.marketingAndOperatingCarriers?.marketingCarrier || initialData.marketingAndOperatingCarriers?.operatingCarrier) {
    searchObj.marketingAndOperatingCarriers = {};
    if (initialData.marketingAndOperatingCarriers?.marketingCarrier?.code) {
      searchObj.marketingAndOperatingCarriers.marketingCarrier = initialData.marketingAndOperatingCarriers.marketingCarrier?.code;
    }
    if (initialData.marketingAndOperatingCarriers?.operatingCarrier?.code) {
      searchObj.marketingAndOperatingCarriers.operatingCarrier = initialData.marketingAndOperatingCarriers.operatingCarrier?.code;
    }
    if (Object.keys(searchObj.marketingAndOperatingCarriers).length === 0) {
      searchObj.marketingAndOperatingCarriers = null;
    }
  }
  if (initialData.brandAndMarket) {
    searchObj.brandAndMarket = initialData.brandAndMarket;
  }

  return searchObj;
};

export const prepareEffectiveDatesToSubmit = (data: any) => {
  return {
    ...data,
    effectiveDateFrom: data?.effectiveDateFrom ? (formatDate(formatToUTCDate(data.effectiveDateFrom), DATE_FORMAT_SERVER) as any) : null,
    effectiveDateTo: data?.effectiveDateTo ? (formatDate(formatToUTCDate(data.effectiveDateTo), DATE_FORMAT_SERVER) as any) : null,
  };
};

export const formatCodeAndLabelObjsToLabels = (objsArr: any) =>
  objsArr
    .reduce((acc: String[], obj: any) => {
      return [...acc, obj.label];
    }, [])
    .join(', ');

export const transformCodeAndLabelData = (data: CodeAndLabel[]) => {
  if (data && data.length) {
    return data.map((dataItem: CodeAndLabel) => ({
      value: dataItem.code,
      label: dataItem.label,
    }));
  }
  return data;
};

export type TransformIdAndLabelDataProps = (Int32IdAndLabel | GuidIdAndLabel)[];
export type TransformIdAndLabelDataItem = Int32IdAndLabel | GuidIdAndLabel;

export const transformIdAndLabelData = (data: TransformIdAndLabelDataProps) => {
  if (data && data.length) {
    return data.map((dataItem: TransformIdAndLabelDataItem) => ({
      value: dataItem.id,
      label: dataItem.label,
      effective: dataItem.effective,
    }));
  }
  return data;
};

export type DateOrTimeObj = LocalDateLocalDateTuple | LocalTimeLocalTimeTuple;

export const sortDateTime = (dt1: DateOrTimeObj, dt2: DateOrTimeObj) => {
  if (dt1.item1 && dt2.item1 && dt1.item1 > dt2.item1) {
    return 1;
  } else if (dt1.item1 === dt2.item1) {
    if (dt1.item2 && dt2.item2 && dt1.item2 > dt2.item2) {
      return 1;
    } else if (dt1.item1 === dt2.item2) {
      return 0;
    }
    return -1;
  }
  return -1;
};

export const sortTimeStringRanges = (dt1: { item1: string; item2: string }, dt2: { item1: string; item2: string }) => {
  let firstDateFrom: string | number = dt1.item1;
  let firstDateTo: string | number = dt1.item2;
  let secondDateFrom: string | number = dt2.item1;
  let secondDateTo: string | number = dt2.item2;

  firstDateFrom = Number.parseInt(firstDateFrom.replace(':', '').replace('.', ''));
  firstDateTo = Number.parseInt(firstDateTo.replace(':', '').replace('.', ''));
  secondDateFrom = Number.parseInt(secondDateFrom.replace(':', '').replace('.', ''));
  secondDateTo = Number.parseInt(secondDateTo.replace(':', '').replace('.', ''));

  if (firstDateFrom && secondDateFrom && firstDateFrom > secondDateFrom) {
    return 1;
  } else if (firstDateFrom === secondDateFrom) {
    if (firstDateTo && secondDateTo && firstDateTo > secondDateTo) {
      return 1;
    } else if (firstDateFrom === secondDateTo) {
      return 0;
    }
    return -1;
  }
  return -1;
};

export const transformLocationsOnlyFromAutocomplete = (data: StationOrLocationItem[]) => {
  if (data && data.length) {
    return data
      .filter((dataItem: StationOrLocationItem) => {
        return dataItem.isLocation === true ? true : false;
      })
      .map((dataItem: StationOrLocationItem) => ({
        value: JSON.stringify({
          code: dataItem.code,
          label: dataItem.label,
          isLocation: dataItem.isLocation,
        }),
        label: `(${dataItem.code}) ${dataItem.label}`,
      }));
  }
  return data;
};

export const transformCountryAutocomplete = (data: TransformIdAndLabelDataProps) => {
  if (data && data.length) {
    return data.map((dataItem: TransformIdAndLabelDataItem) => ({
      value: JSON.stringify({
        code: dataItem.label?.substring(dataItem.label?.indexOf('(') + 1, dataItem.label?.indexOf(')')),
        label: dataItem.label?.substring(dataItem.label?.indexOf(')') + 2),
        effective: dataItem.effective,
      }),
      label: dataItem.label,
    }));
  }
  return data;
};

export const transformAirlineAutocomplete = (data: { code: string; id: string; label: string; effective: boolean }[]) => {
  if (data && data.length) {
    return data.map((dataItem: { code: string; id: string; label: string; effective: boolean }) => ({
      value: JSON.stringify({
        code: dataItem.code,
        label: dataItem.label,
      }),
      label: dataItem.label,
    }));
  }
  return data;
};

export const validateLatitudeString = (value: string) => {
  const parsedNumber = Number.parseFloat(value);

  if (value.trim() === parsedNumber.toString() && parsedNumber >= -90 && parsedNumber <= 90) {
    return true;
  }

  return false;
};

export const validateLongitudeString = (value: string) => {
  const parsedNumber = Number.parseFloat(value);

  if (value.trim() === parsedNumber.toString() && parsedNumber >= -180 && parsedNumber <= 180) {
    return true;
  }

  return false;
};

export const parseIntergerFromInput = (value: string | number | undefined) => {
  if (typeof value === 'string') {
    return Number.parseInt(value);
  }
  return value;
};

export const parseJSONValueFromAutocomplete = (value: any) => {
  try {
    const parsedVal = JSON.parse(value);
    if (parsedVal.value !== undefined && isNaN(Number(parsedVal.value))) {
      try {
        return JSON.parse(parsedVal.value);
      } catch (e) {
        return parsedVal;
      }
    }
    return parsedVal;
  } catch (e) {
    return value;
  }
};

export const formatToUTCDate = (date: object | Date | string | undefined | null) => {
  if (date instanceof Date) {
    const timeZoneOffset = date.getTimezoneOffset();
    const time = date.getTime();
    if (timeZoneOffset > 0) {
      const utcDate: any = new Date(time + timeZoneOffset * 60 * 1000);
      utcDate.setUTCHours(0, 0, 0, 0);
      return utcDate;
    } else {
      const utcDate: any = new Date(time - timeZoneOffset * 60 * 1000);
      utcDate.setUTCHours(0, 0, 0, 0);
      return utcDate;
    }
  }
  return date;
};

export const getTrimmedString = (value: string | undefined | null) => {
  if (typeof value === 'string') {
    return value.trim();
  }
  return value;
};

export const lowerCaseFirstLetter = (str: string) => str.charAt(0).toLowerCase() + str.slice(1);

export const getObjWithoutNullValues = (obj: any) => {
  const outputObj: any = {};
  Object.keys(obj).forEach((item) => {
    const val = obj[item];
    if (val !== null) {
      outputObj[item] = val;
    }
  });
  return outputObj;
};

export const getFilterItemByValue = ({ filters, value, searchField = '', valueFallback = null }: GetFilterItemByValueOpts) => {
  if (!filters || value === undefined || !searchField) return valueFallback;
  let output: any = valueFallback;
  for (let i = 0; i < filters.length; i++) {
    if (typeof value === 'object' && filters[i].value === value.id) {
      output = filters[i][searchField];
      break;
    }

    if (filters[i].value === value) {
      output = filters[i][searchField];
      break;
    }
  }
  return output;
};

export const convertMarkupTimeObjToSMTimeObj = (timeObj: TimeOfDayRange) => {
  const fromHours = `${timeObj?.from?.hours}`.length === 1 ? `0${timeObj?.from?.hours}` : `${timeObj?.from?.hours}`;
  const fromMinutes = `${timeObj?.from?.minutes}`.length === 1 ? `0${timeObj?.from?.minutes}` : `${timeObj?.from?.minutes}`;
  const toHours = `${timeObj?.to?.hours}`.length === 1 ? `0${timeObj?.to?.hours}` : `${timeObj?.to?.hours}`;
  const toMinutes = `${timeObj?.to?.minutes}`.length === 1 ? `0${timeObj?.to?.minutes}` : `${timeObj?.to?.minutes}`;

  const item1 = `${fromHours}:${fromMinutes}:00.001`;
  const item2 = `${toHours}:${toMinutes}:00.999`;
  return { item1, item2 };
};

export const convertSMTimeObjToMarkupTimeObj = (timeObj: { item1: string; item2: string }) => {
  const fromHours = Number.parseInt(timeObj?.item1?.slice(0, 2));
  const fromMinutes = Number.parseInt(timeObj?.item1?.slice(3, 5));
  const toHours = Number.parseInt(timeObj?.item2?.slice(0, 2));
  const toMinutes = Number.parseInt(timeObj?.item2?.slice(3, 5));

  return {
    from: { hours: fromHours, minutes: fromMinutes },
    to: { hours: toHours, minutes: toMinutes },
  };
};

//Changed ThresholdDate type to Dayjs
const disableDatesFrom = (date: dayjs.Dayjs | string | undefined | null, thresholdDate: dayjs.Dayjs | object | string | undefined | null) => {
  if (dayjs.isDayjs(date) && dayjs.isDayjs(thresholdDate)) {
    const dateObj = date.toDate();
    if (dateObj.toDateString() === thresholdDate.toDate().toDateString()) return true;
    return date.valueOf() < thresholdDate.valueOf();
  }
  return false;
};

//Changed ThresholdDate type to Dayjs
const disableDatesTo = (date: dayjs.Dayjs | string | undefined | null, thresholdDate: dayjs.Dayjs | object | string | undefined | null) => {
  if (dayjs.isDayjs(date) && dayjs.isDayjs(thresholdDate)) {
    const dateObj = date.toDate();
    if (dateObj.toDateString() === thresholdDate.toDate().toDateString()) return true;
    return date.valueOf() > thresholdDate.valueOf();
  }
  return false;
};

//Changed ThresholdDate type to Dayjs
export const disableDates = (
  date: dayjs.Dayjs | undefined | null,
  thresholdDate: dayjs.Dayjs | string | undefined | null,
  disableDatesType: 'from' | 'to' | null | undefined
) => {
  if (disableDatesType === 'from') {
    return disableDatesFrom(date, thresholdDate);
  }
  if (disableDatesType === 'to') {
    return disableDatesTo(date, thresholdDate);
  }

  return false;
};

export const transformCarriersAutocompleteData = (data: GuidIdCodeAndLabel[]) => {
  if (data && data.length) {
    return data.map((dataItem: GuidIdCodeAndLabel) => ({
      id: dataItem.id,
      value: JSON.stringify(dataItem),
      label: dataItem.label,
      effective: dataItem.effective,
    }));
  }
  return data;
};

export const transformLocationsAutocompleteData = (data: GuidIdCodeAndLabel[]) => {
  if (data && data.length) {
    return data.map((dataItem: GuidIdCodeAndLabel) => ({
      id: dataItem.id,
      value: dataItem.code,
      label: dataItem.label,
      effective: dataItem.effective,
    }));
  }
  return data;
};

export const transformAutocompleteAgency = (data: Array<AgencyAutocompleteResponseItem>) => {
  if (data && data.length) {
    return data.map((dataItem: AgencyAutocompleteResponseItem) => ({
      value: JSON.stringify(dataItem.agency),
      label: `(${dataItem.agency?.code}) ${dataItem.agency?.label}`,
      effective: dataItem.agency?.effective,
      agencyGroup: dataItem.agencyGroup,
    }));
  }
  return data;
};

export const transformBrandAndMarketUserPermissionsAutocomplete = (data: BrandAndMarketListItem[]) => {
  if (data && data.length) {
    return data.map((dataItem: BrandAndMarketListItem) => ({
      value: dataItem,
      label: `${dataItem?.brand?.label} (${dataItem?.market?.label})`,
      effective: dataItem?.allowEdit,
    }));
  }
  return data;
};

export const scrollTop = (y: number = 0) => {
  window.scrollTo({
    top: y,
    behavior: 'smooth',
  });
};

export const downloadFile = (data: any, fileName?: string, mime?: string) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: mime || MIME_TYPES.CSV }));
  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = url;
  if (typeof link.download === 'undefined') {
    link.setAttribute('target', '_blank');
  } else {
    link.setAttribute('download', fileName || '');
  }
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 500);
};

export const prepareSuggestionsWithCustomLabels = (secondaryFieldName: string) => {
  return function (suggestions: any) {
    return suggestions.map((suggestion: any) => {
      const parsedVal = parseJSONValueFromAutocomplete(suggestion.value);
      const isSecondaryDetect = typeof parsedVal === 'object' ? parsedVal[secondaryFieldName] : suggestion[secondaryFieldName];
      return {
        value: JSON.stringify(suggestion),
        key: suggestion.value + suggestion.label,
        label: createElement(AutocompleteOptionLabel, { isSecondary: isSecondaryDetect === false }, suggestion.label),
      };
    });
  };
};

export const checkIsSecondaryItem = (item: any, secondaryFieldName: string) => {
  if (!item) return false;
  const secondaryVal = item[secondaryFieldName];
  return secondaryVal !== undefined && secondaryVal === false;
};

export const convertDateToShortUTCString = (date: any) => {
  if (date instanceof Date) {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  }
  return date;
};

export const convertAgencyGroupToGuidIdAndLabel = (agencyGroup: GuidFilterItemOutput) => {
  return {
    effective: agencyGroup?.isEffective || '',
    id: agencyGroup?.value || '',
    label: agencyGroup?.displayText || '',
  };
};

export const getAgencyGroupsArrayOfGuidIdAndLabel = (agencyGroupsArray: GuidFilterItemOutput[] | null | undefined) => {
  if (!agencyGroupsArray) return;
  return agencyGroupsArray.map((agencyGroup) => convertAgencyGroupToGuidIdAndLabel(agencyGroup));
};

export const getFormattedDate = (date: any, format: Format = Format.Utc) => {
  let year = format === Format.Utc ? date.getUTCFullYear() : date.getFullYear();
  let month = format === Format.Utc ? (1 + date.getUTCMonth()).toString() : (1 + date.getMonth()).toString();
  let day = format === Format.Utc ? date.getUTCDate().toString() : date.getDate().toString();

  month = month.length > 1 ? month : '0' + month;
  day = day.length > 1 ? day : '0' + day;

  return year + '-' + month + '-' + day;
};

export const getFormattedTime = (date: any, format: Format = Format.Utc) => {
  let hours = format === Format.Utc ? date.getUTCHours() : date.getHours();
  let minutes = format === Format.Utc ? date.getUTCMinutes() : date.getMinutes();
  let seconds = format === Format.Utc ? date.getUTCSeconds() : date.getSeconds();

  hours = hours < 10 ? '0' + hours : hours;
  minutes = minutes < 10 ? '0' + minutes : minutes;
  seconds = seconds < 10 ? '0' + seconds : seconds;

  return hours + ':' + minutes + ':' + seconds;
};

export const getFormattedDateAndTimeFromString = (date: any) => {
  if (!date) return '';
  const [left, right] = date.split('T');
  return `${left} ${right.slice(0, 8)}`;
};

export const getFormattedDateFromString = (date: any) => {
  if (!date) return '';
  const [left] = date.split('T');
  return `${Number(left.slice(8, 10))} ${MONTHS[Number(left.slice(5, 7)) - 1]} ${left.slice(0, 4)}`;
};

export const getFormattedTimeFromString = (date: any) => {
  if (!date) return '';
  const right = date.split('T')[1];
  return right.slice(0, 8);
};

export const getUserPermissionsSubmitData = (data?: IPermissionsData): UserPermissionsFormData => {
  const getArrayToSubmit = (items?: Array<IPermissionListItem>): Array<PermissionListItem> | undefined => {
    if (!items || !items.length) return undefined;
    return items.map((item) => ({
      brandAndMarket: {
        brand: item.brandAndMarket?.brand?.id,
        market: item.brandAndMarket?.market?.id,
      },
      restriction: item.restriction?.id,
    }));
  };

  return {
    adminPortal: data?.adminPortal?.id,
    userPermissions: data?.userPermissions?.id,
    regions: data?.regions?.id,
    countries: data?.countries?.id,
    cities: data?.cities?.id,
    airports: data?.airports?.id,
    airlines: data?.airlines?.id,
    shoppingDiagnostics: data?.shoppingDiagnostics?.id,
    markupDiagnostics: data?.markupDiagnostics?.id,
    brands: data?.brands?.id,
    markets: data?.markets?.id,
    distributionChannels: data?.distributionChannels?.id,
    searchManagerDiagnostics: data?.searchManagerDiagnostics?.id,
    activityLog: data?.activityLog?.id,
    technicalLogs: data?.technicalLogs?.id,
    supplierLocationMappings: data?.geoGroups?.id,
    providers: data?.suppliers?.id,
    ancillarySuppliers: data?.ancillarySuppliers?.id,
    ancillaryDistributions: getArrayToSubmit(data?.ancillaryDistributions || undefined),
    searchManager: getArrayToSubmit(data?.searchManager || undefined),
    bookingCreateManagerOsis: getArrayToSubmit(data?.bookingCreateManagerOsis || undefined),
    bookingCreateManagerRemarks: getArrayToSubmit(data?.bookingCreateManagerRemarks || undefined),
    bookingTolerances: getArrayToSubmit(data?.bookingTolerances || undefined),
    agencies: getArrayToSubmit(data?.agencies || undefined),
    agencyGroups: getArrayToSubmit(data?.agencyGroups || undefined),
    providerCredentials: getArrayToSubmit(data?.providerCredentials || undefined),
    providerPreferences: getArrayToSubmit(data?.providerPreferences || undefined),
    identityProviderSettings: getArrayToSubmit(data?.identityProviderSettings || undefined),
    identitySettings: getArrayToSubmit(data?.identitySettings || undefined),
    markups: getArrayToSubmit(data?.markups || undefined),
    ticketTLO: getArrayToSubmit(data?.ticketTLO || undefined),
    globalFilters: getArrayToSubmit(data?.globalFilters || undefined),
    vccCredentials: getArrayToSubmit(data?.vccCredentials || undefined),
    ssrRules: getArrayToSubmit(data?.ssrRules || undefined),
    ancillaryManager: data?.ancillaryManager?.id,
  };
};

export const getArrayItemForUserPermissionsEditor = (item: PermissionListItem) => ({
  brandAndMarket: {
    brand: {
      id: item.brandAndMarket?.brand,
    },
    market: {
      id: item.brandAndMarket?.market,
    },
  },
  restriction: {
    id: item.restriction,
  },
});

export const getPermissionsForUserPermissionsEditor = (permissions?: UserPermissionsFormData): IPermissionsData => {
  return {
    adminPortal: { id: permissions?.adminPortal },
    userPermissions: { id: permissions?.userPermissions },
    searchManager: permissions?.searchManager?.map(getArrayItemForUserPermissionsEditor),
    searchManagerDiagnostics: { id: permissions?.searchManagerDiagnostics },
    markupDiagnostics: { id: permissions?.markupDiagnostics },
    bookingCreateManagerOsis: permissions?.bookingCreateManagerOsis?.map(getArrayItemForUserPermissionsEditor),
    bookingCreateManagerRemarks: permissions?.bookingCreateManagerRemarks?.map(getArrayItemForUserPermissionsEditor),
    bookingTolerances: permissions?.bookingTolerances?.map(getArrayItemForUserPermissionsEditor),
    regions: { id: permissions?.regions },
    countries: { id: permissions?.countries },
    cities: { id: permissions?.cities },
    airports: { id: permissions?.airports },
    airlines: { id: permissions?.airlines },
    agencies: permissions?.agencies?.map(getArrayItemForUserPermissionsEditor),
    agencyGroups: permissions?.agencyGroups?.map(getArrayItemForUserPermissionsEditor),
    providerCredentials: permissions?.providerCredentials?.map(getArrayItemForUserPermissionsEditor),
    providerPreferences: permissions?.providerPreferences?.map(getArrayItemForUserPermissionsEditor),
    identityProviderSettings: permissions?.identityProviderSettings?.map(getArrayItemForUserPermissionsEditor),
    identitySettings: permissions?.identitySettings?.map(getArrayItemForUserPermissionsEditor),
    markups: permissions?.markups?.map(getArrayItemForUserPermissionsEditor),
    ticketTLO: permissions?.ticketTLO?.map(getArrayItemForUserPermissionsEditor),
    shoppingDiagnostics: { id: permissions?.shoppingDiagnostics },
    brands: { id: permissions?.brands },
    markets: { id: permissions?.markets },
    distributionChannels: { id: permissions?.distributionChannels },
    globalFilters: permissions?.globalFilters?.map(getArrayItemForUserPermissionsEditor),
    activityLog: { id: permissions?.activityLog },
    technicalLogs: { id: permissions?.technicalLogs },
    geoGroups: { id: permissions?.supplierLocationMappings },
    suppliers: { id: permissions?.providers },
    ancillarySuppliers: { id: permissions?.ancillarySuppliers },
    ancillaryDistributions: permissions?.ancillaryDistributions?.map(getArrayItemForUserPermissionsEditor),
    vccCredentials: permissions?.vccCredentials?.map(getArrayItemForUserPermissionsEditor),
    ssrRules: permissions?.ssrRules?.map(getArrayItemForUserPermissionsEditor),
    ancillaryManager: { id: permissions?.ancillaryManager },
  };
};

export const getUpdatedPermissions = (data?: IPermissionsData, configuration?: UserPermissionsConfiguration): IPermissionsData => {
  function MapArray(items: Array<IPermissionListItem> | undefined | null) {
    return items?.map((item) => ({
      brandAndMarket: configuration?.brandsAndMarkets?.find(
        (brandAndMarket) => brandAndMarket?.brand?.id === item.brandAndMarket?.brand?.id && brandAndMarket?.market?.id === item.brandAndMarket?.market?.id
      ),
      restriction: configuration?.fieldPermissions?.find((permission) => permission.id === item?.restriction?.id),
    }));
  }

  return {
    adminPortal: configuration?.allowedDeniedPermissions?.find((item) => item.id === data?.adminPortal?.id),
    userPermissions: configuration?.sectionPermissions?.find((item) => item.id === data?.userPermissions?.id),
    regions: configuration?.sectionPermissions?.find((item) => item.id === data?.regions?.id),
    countries: configuration?.sectionPermissions?.find((item) => item.id === data?.countries?.id),
    cities: configuration?.sectionPermissions?.find((item) => item.id === data?.cities?.id),
    airports: configuration?.sectionPermissions?.find((item) => item.id === data?.airports?.id),
    airlines: configuration?.sectionPermissions?.find((item) => item.id === data?.airlines?.id),
    shoppingDiagnostics: configuration?.allowedDeniedPermissions?.find((item) => item.id === data?.shoppingDiagnostics?.id),
    markupDiagnostics: configuration?.allowedDeniedPermissions?.find((item) => item.id === data?.markupDiagnostics?.id),
    brands: configuration?.sectionPermissions?.find((item) => item.id === data?.brands?.id),
    markets: configuration?.sectionPermissions?.find((item) => item.id === data?.markets?.id),
    distributionChannels: configuration?.sectionPermissions?.find((item) => item.id === data?.distributionChannels?.id),
    searchManagerDiagnostics: configuration?.allowedDeniedPermissions?.find((item) => item.id === data?.searchManagerDiagnostics?.id),
    activityLog: configuration?.allowedDeniedPermissions?.find((item) => item.id === data?.activityLog?.id),
    technicalLogs: configuration?.allowedDeniedPermissions?.find((item) => item.id === data?.technicalLogs?.id),
    geoGroups: configuration?.sectionPermissions?.find((item) => item.id === data?.geoGroups?.id),
    suppliers: configuration?.sectionPermissions?.find((item) => item.id === data?.suppliers?.id),
    ancillarySuppliers: configuration?.sectionPermissions?.find((item) => item.id === data?.ancillarySuppliers?.id),
    ancillaryDistributions: MapArray(data?.ancillaryDistributions),
    searchManager: MapArray(data?.searchManager),
    bookingCreateManagerOsis: MapArray(data?.bookingCreateManagerOsis),
    bookingCreateManagerRemarks: MapArray(data?.bookingCreateManagerRemarks),
    bookingTolerances: MapArray(data?.bookingTolerances),
    agencies: MapArray(data?.agencies),
    agencyGroups: MapArray(data?.agencyGroups),
    providerCredentials: MapArray(data?.providerCredentials),
    providerPreferences: MapArray(data?.providerPreferences),
    identityProviderSettings: MapArray(data?.identityProviderSettings),
    identitySettings: MapArray(data?.identitySettings),
    markups: MapArray(data?.markups),
    ticketTLO: MapArray(data?.ticketTLO),
    globalFilters: MapArray(data?.globalFilters),
    vccCredentials: MapArray(data?.vccCredentials),
    ssrRules: MapArray(data?.ssrRules),
    ancillaryManager: configuration?.sectionPermissions?.find((item) => item.id === data?.ancillaryManager?.id),
  };
};

export const nullableStringToOptional = (val?: string | null) => (val ? val : undefined);

export const isObject = (obj: any) => typeof obj === 'object';

export const isEqualObjects = (obj1: any, obj2: any): boolean => {
  if (!isObject(obj1) || !isObject(obj2) || obj1 === undefined || obj2 === undefined || obj1 === null || obj2 === null) return false;
  const obj1Keys = Object.keys(obj1);
  const obj2Keys = Object.keys(obj2);
  if (obj1Keys.length !== obj2Keys.length) return false;
  for (const key of obj1Keys) {
    if (!obj2Keys.includes(key)) return false;
    if (typeof obj1[key] === 'object') {
      if (!isEqualObjects(obj1[key], obj2[key])) return false;
      else continue;
    }
    if (obj1[key] !== obj2[key]) return false;
  }
  return true;
};

export function usePrevious(value: any) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export const getClassNamesUnion = (classNames: Array<any>) => classNames.filter((item: any) => item !== null) && classNames.join(' ');

export const getBrandAndMarketFromKey = (key: string, items: Array<BrandAndMarketListItem> | undefined | null) => {
  const splits = key.split('-');
  const brandId = Number(splits[0]);
  const marketId = splits[1];
  const brandAndMarket = items?.find((item) => item.brand?.id === brandId && item.market?.id === marketId);
  return brandAndMarket;
};

export const getBrandAndMarketWithPermissionFromKey = (key: string, items: Array<BrandAndMarketListItemFilterItemOutputWithPermission> | undefined | null) => {
  const splits = key.split('-');
  const brandId = Number(splits[0]);
  const marketId = splits[1];
  const brandAndMarket = items?.find((item) => item.value?.brand?.id === brandId && item.value?.market?.id === marketId);
  return brandAndMarket;
};

export const generateRandomId = () => {
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
};

export const sortPropsAndStringify = (data: any) => {
  const sortedKeys = Object.keys(data).sort();
  const sortedEntries = sortedKeys.map((key) => [key.toLowerCase(), data[key]]);
  const sortedObject = Object.fromEntries(sortedEntries);
  return JSON.stringify(sortedObject);
};

export const sortPropsAndStringifyOsis = (data: any) => {
  const sortedKeys = Object.keys(data).sort();
  const sortedEntries = sortedKeys.map((key) => [
    key.toLowerCase(),
    key !== 'fareTypes' ? data[key] : data[key]?.sort((a: any, b: any) => a.code.toLowerCase().localeCompare(b.code.toLowerCase())),
  ]);
  const sortedObject = Object.fromEntries(sortedEntries);
  return JSON.stringify(sortedObject);
};

export const getRandomKey = () => {
  return Math.random().toString(36).substring(2);
};

export const removeDuplicateElementFromArray = (inputArr: any[]) => {
  return inputArr.filter((obj: any, index, arr) => {
    return arr.findIndex((item: any) => item.id === obj.id) === index;
  });
};

export const removeDuplicateElementFromConnectionTimeArray = (inputArr: any[]) => {
  return inputArr.filter((obj: any, index, arr) => {
    if (obj.station) {
      return arr.findIndex((item: any) => item.station.id === obj.station.id) === index;
    } else {
      return arr.findIndex((item: any) => item.location.id === obj.location.id) === index;
    }
  });
};
